import { ComponentType, ReactNode, useEffect } from 'react';
import { Credentials, CredentialsEntity } from '../../../models/credentials';
import { useAppDispatch, useAppSelector } from '../../../stores/redux-store';
import { FlowFinished } from '../FlowFinished/FlowFinished';
import { ProviderEntity } from '../../../models/provider';
import { RefreshableItem } from '../../../models/refreshableItem';
import { BackButton } from '../../organisms/Header/HeaderMolecule';
import { useIsFlowHandledByRedirectHandler as useIsFlowHandledByRedirectHandlerImport } from '../../../utilities/useIsFlowHandledByRedirectHandler';
import { applicationParameters } from '../../../utilities/application-parameters';

import { useCredentialsMachineRollout } from '../../../connectivity/hooks/useCredentialsMachineRollout';
import { setCredentials } from '../../../stores/credentials-store';
import {
  AuthorizeCredentials,
  CredentialsCreatorMachineViewProps,
} from '../../../connectivity/components/CredentialsCreatorMachineView';
import { CredentialsCopyContext } from './hooks/useCredentialsCopy';
import { SubmitCredentialsProps, SubmitCredentialsWrapper } from './SubmitCredentialsWrapper';

/**
 * This is a workaround component that holds an effect that would've been
 * triggered conditionally from the parent.
 */
const OnDoneEffect = ({
  credentials,
  onDone,
}: {
  credentials: CredentialsEntity;
  onDone: (credentials: CredentialsEntity) => void;
}) => {
  useEffect(() => {
    onDone(credentials);
  }, [credentials, onDone]);

  return null;
};

export type ReduxCredentialsProps = {
  /**
   * Customize the main title and initializeProductLoading on the connectivity screens.
   * Not all screens may show the text.
   *
   * If not passed in, generic copy will be used.
   */
  heading?: ReactNode;
  initializeProductLoading?: ReactNode;

  provider: ProviderEntity;
  /**
   * If not passed in, the component will automatically calculate them.
   *
   * @see getProviderRefreshableItems
   */
  refreshableItems: RefreshableItem[];
  /**
   * Whether we should wait for credentials to be UPDATED, or just UPDATING.
   *
   * It's up to you to set this correctly, potentially reading the TL config to
   * look for feature flags.
   */
  waitForUpdated?: boolean;
  // Make sure this is stable e.g. wrapped in `useCallback`.
  onDone: (credentials: CredentialsEntity) => void;
  triggerRefresh?: boolean;
  SubmitCredentialsComponent?: ComponentType<SubmitCredentialsProps>;
  useIsFlowHandledByRedirectHandler?: typeof useIsFlowHandledByRedirectHandlerImport;
  enableAutoSubmitCredentials?: boolean;
} & BackButton &
  Partial<
    Pick<
      CredentialsCreatorMachineViewProps,
      'providedCredentials' | 'onCredentialsCreated' | 'onHandoffUpdated'
    >
  >;

/**
 * Embed this component into your XState views to create credentials.
 *
 * If this component needs to redirect out to the bank, it will come back to the
 * original URL and append some parameters, which it will read by itself. Any
 * parameters that were present in the URL before the redirect will be preserved,
 * so you can use those to resume the parent machine at the node handling credentials.
 *
 * Parts of this component tree are gradually being migrated to XState, but you
 * shouldn't have to worry about that.
 *
 * @example
 *     redirect from bank to original URL
 *    ┌──────────────────────────────────┐
 *    │                                  │
 *    ▼                                  │
 * ┌─────┐          ┌─────┐        ┌─────┴─────┐       ┌─────┐
 * │start├─────────►│stuff├───────►│credentials├──────►│stuff│
 * └──┬──┘          └─────┘        └───────────┘       └─────┘
 *    │                                  ▲
 *    └──────────────────────────────────┘
 *        skip ahead to credentials
 */
export const ReduxCredentials = ({
  heading,
  initializeProductLoading,
  provider,
  refreshableItems,
  waitForUpdated = true,
  enableAutoSubmitCredentials = true,
  onBack,
  onDone,
  triggerRefresh,
  providedCredentials,
  onCredentialsCreated,
  onHandoffUpdated,
  SubmitCredentialsComponent = SubmitCredentialsWrapper,
  useIsFlowHandledByRedirectHandler = useIsFlowHandledByRedirectHandlerImport,
}: ReduxCredentialsProps) => {
  const dispatch = useAppDispatch();
  const userCredentials = useAppSelector((state) => state.userCredentials.data[0]);
  const credentials = useAppSelector((state) => state.credentials.data);
  const redirectedCredentialsId = useAppSelector(
    (state) => state.application.redirectedCredentialsId
  );

  const isUpdated = Credentials.isUpdated(credentials);
  const canContinueFlowFinished = isUpdated || Credentials.isUpdating(credentials);
  const isDone = waitForUpdated ? isUpdated : canContinueFlowFinished;
  const flowFinished = useIsFlowHandledByRedirectHandler({
    redirectedCredentialsId,
    appUri: applicationParameters.get('appUri'),
    credentials,
  });
  const isCredentialsMachineEnabled = useCredentialsMachineRollout(provider);

  if (isCredentialsMachineEnabled) {
    return (
      <AuthorizeCredentials
        heading={heading}
        initializeProductLoading={initializeProductLoading}
        onDone={(updatedCredentials) => {
          // some products don't use onDone but still reads from credentials from redux
          dispatch(setCredentials(updatedCredentials));
          onDone(updatedCredentials);
        }}
        waitForUpdated={waitForUpdated}
        // if undefined will hide back button
        onBack={onBack}
        provider={provider}
        // product can disable autoSubmit if going back is supported
        enableAutoSubmitCredentials={enableAutoSubmitCredentials}
        triggerRefresh={triggerRefresh}
        refreshableItems={refreshableItems}
        onCredentialsCreated={onCredentialsCreated}
        onHandoffUpdated={onHandoffUpdated}
        // product can provide credentials to authorise
        providedCredentials={providedCredentials || credentials}
        // refresh credentials for perm user
        userCredentials={userCredentials}
      />
    );
  }

  if (credentials) {
    if (canContinueFlowFinished) {
      if (flowFinished) {
        return <FlowFinished />;
      }
    }

    if (isDone) {
      return <OnDoneEffect credentials={credentials} onDone={onDone} />;
    }
  }

  return (
    <CredentialsCopyContext.Provider value={{ heading }}>
      <SubmitCredentialsComponent
        provider={provider}
        refreshableItems={refreshableItems}
        onBack={onBack}
        triggerRefresh={triggerRefresh}
        enableAutoSubmitCredentials={enableAutoSubmitCredentials}
      />
    </CredentialsCopyContext.Provider>
  );
};
